import type { ReactNode } from 'react'
import { useState } from 'react'
import { createNonNullableContext } from '@joyful/utils'
import {
  AppShell,
  AppShellBody,
  AppShellContent,
  DelayedSpinner,
  PageMain,
  Stack,
  VisuallyHidden,
} from '../../core'
import {
  NavbarBrand,
  NavbarEnvironmentStripe,
  NavbarFrame,
  NavbarSection,
} from './navbar'
import type { NavbarEnvironment } from './navbar'

const DEFAULT_PUBLIC_PATH_PREFIXES = [
  '/sign-in',
  '/cli-auth/callback',
  '/extension/auth-complete',
] as const

interface InitialPageLoadActions {
  completeInitialPageLoad: () => void
}

const InitialPageLoadContext = createNonNullableContext<InitialPageLoadActions>(
  'InitialPageLoadContext',
)

export interface InitialPageLoadProviderProps {
  children: ReactNode
  environment: NavbarEnvironment
}

export function InitialPageLoadProvider({
  children,
  environment,
}: InitialPageLoadProviderProps) {
  const [isInitialPageLoadComplete, setIsInitialPageLoadComplete] =
    useState(isPublicInitialPath)

  return (
    <InitialPageLoadContext.Context
      value={{
        completeInitialPageLoad: () => {
          setIsInitialPageLoadComplete(true)
        },
      }}
    >
      {isInitialPageLoadComplete ? null : (
        <InitialPageLoadShell environment={environment} />
      )}
      {children}
    </InitialPageLoadContext.Context>
  )
}

export interface InitialPageLoadCompleteProps {
  children: ReactNode
}

export function InitialPageLoadComplete({
  children,
}: InitialPageLoadCompleteProps) {
  const initialPageLoadContext = InitialPageLoadContext.useRequiredContext()

  return (
    <>
      <span
        ref={(element) => {
          if (element) {
            initialPageLoadContext.completeInitialPageLoad()
          }
        }}
        hidden
      />
      {children}
    </>
  )
}

export function InitialPageLoadHydrateFallback() {
  return null
}

function InitialPageLoadShell({
  environment,
}: {
  environment: NavbarEnvironment
}) {
  return (
    <>
      <VisuallyHidden aria-busy="true" role="status">
        {`Loading…`}
      </VisuallyHidden>
      <div
        aria-hidden="true"
        className="bg-tremor-background fixed inset-0 z-50"
      >
        <AppShell>
          <NavbarFrame
            startAdornment={
              <NavbarEnvironmentStripe environment={environment} />
            }
          >
            <NavbarSection>
              <NavbarBrand>{`Joyful Health`}</NavbarBrand>
            </NavbarSection>
          </NavbarFrame>
          <AppShellBody>
            <AppShellContent>
              <PageMain background="warm" grow="fill" padding="none">
                <Stack align="center" grow="fill" justify="center">
                  <DelayedSpinner delayMs={1500} />
                </Stack>
              </PageMain>
            </AppShellContent>
          </AppShellBody>
        </AppShell>
      </div>
    </>
  )
}

function isPublicInitialPath() {
  const pathname = window.location.pathname

  return DEFAULT_PUBLIC_PATH_PREFIXES.some((prefix) => {
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}
